import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Button, Box, Tooltip } from '@mui/material'
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { StepsHeader } from '../../../components'
import { useTranslation } from "react-i18next"

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`

const TimeContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 32px;
`

const TimeSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  > span + span {
    margin-bottom: 24px;
  }
`

const Footer = styled(Box)`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`

const IntakeSleep = ({
  sleepTime,
  awakeTime,
  onSleepChange,
  onAwakeChange,
  step,
  onNext,
  onPrevious,
}) => {
  const [minDate, setMinDate] = useState(null)
  const [maxDate, setMaxDate] = useState(null)
  const [error, setError] = useState(null)
  const { t } = useTranslation('common')

  const errorOptions = {
    awakeTimeBiggerThanSleepTime:
      t('sleep.invalid_input'),
    hasEmptyDate: t('sleep.no_input'),
  }

  useEffect(() => {
    if (!minDate) setMinDate(sleepTime - (1000 * 60 * 60 * 24 * 2))

    const sleepTimeMS = new Date(sleepTime).getTime()
    const newMaxDate = sleepTimeMS + 1000 * 60 * 60 * 24 * 2
    if (!maxDate) setMaxDate(new Date(newMaxDate))
  })

  useEffect(() => {
    if (sleepTime >= awakeTime) {
      setError(errorOptions.awakeTimeBiggerThanSleepTime)
      return
    }

    if (!sleepTime || !awakeTime) {
      setError(errorOptions.hasEmptyDate)
      return
    }

    setError(null)
  }, [sleepTime, awakeTime])

  const disableForwardButton = !sleepTime || !awakeTime || error

  return (
    <Container>
      <div>
        <StepsHeader
          stepText={step}
          description={t('sleep.start.description')}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <TimeContainer>
            <TimeSelectionContainer>
              <span
                style={{ fontSize: 24, fontWeight: 700, textAlign: 'center' }}
              >
                {t('sleep.start.question')}
              </span>
              <span style={{ fontSize: 18, textAlign: 'center' }}>
                {t('sleep.start.input_request')}
              </span>
              <MobileDateTimePicker
                label={t('sleep.start.datepicker')}
                ampm={false}
                minutesStep={15}
                value={sleepTime}
                onChange={newValue => onSleepChange(newValue)}
                minDate={new Date(minDate)}
                maxDate={new Date(minDate)}
                views={['day', 'hours', 'minutes']}
                openTo='hours'
                orientation='landscape'
                format='dd/MM - HH:mm'
              />
            </TimeSelectionContainer>
            <TimeSelectionContainer>
              <span
                style={{ fontSize: 24, fontWeight: 700, textAlign: 'center' }}
              >
                {t('sleep.awoke.question')}
              </span>
              <span style={{ fontSize: 18, textAlign: 'center' }}>
                {t('sleep.awoke.input_request')}
              </span>
              <MobileDateTimePicker
                label={t('sleep.awoke.datepicker')}
                ampm={false}
                minutesStep={15}
                value={awakeTime}
                onChange={newValue => onAwakeChange(newValue)}
                minDate={new Date(minDate)}
                maxDate={new Date(maxDate)}
                views={['day', 'hours', 'minutes']}
                openTo='hours'
                orientation='landscape'
                format='dd/MM - HH:mm'
              />
            </TimeSelectionContainer>
          </TimeContainer>

          {error && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <span
                style={{
                  fontSize: 16,
                  marginTop: 40,
                  fontStyle: 'italic',
                  color: '#BE3756',
                }}
              >
                {error}
              </span>
            </div>
          )}
        </LocalizationProvider>
      </div>
      <Footer>
        <Button
          variant='text'
          onClick={onPrevious}
          startIcon={<ArrowBackIcon />}
        >
        {t('sleep.return')}
        </Button>
        <div>
          {disableForwardButton ? (
            <Tooltip
              title={error}
              placement='top'
              arrow
            >
              <span>
                <Button
                  variant='contained'
                  disabled
                >
                  {t('sleep.advance')}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button
              variant='contained'
              onClick={onNext}
            >
              {t('sleep.advance')}
            </Button>
          )}
        </div>
      </Footer>
    </Container>
  )
}

export default IntakeSleep
